import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../commonDesign/Button'
import Icon from '../../../../../../atoms/Icon'
import SourceDiffModal from './SourceDiffModal'

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid var(--color-ai-200)',
    background: 'linear-gradient(135deg, var(--color-ai-050) 0%, white 100%)'
  },
  titleRow: {
    color: 'var(--color-ai-700)'
  },
  description: {
    color: 'var(--color-neutral-600)'
  },
  charCount: {
    color: 'var(--color-neutral-500)'
  },
  divider: {
    height: '1px',
    background: 'var(--color-ai-200)'
  },
  applied: {
    border: '1px solid var(--color-success-300)',
    background: 'var(--color-success-050)'
  },
  appliedLabel: {
    color: 'var(--color-success-600)',
    fontWeight: 600
  },
  dismissed: {
    border: '1px dashed var(--color-neutral-300)',
    background: 'var(--color-neutral-050)'
  },
  dismissedLabel: {
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic'
  }
}))

function ProposedSmartEndpointCard ({ content, description, currentSource, onApply, autoAccept }) {
  const classes = useStyles()
  const [state, setState] = useState('pending')
  const [showDiff, setShowDiff] = useState(false)
  const [copied, setCopied] = useState(false)
  const autoApplied = useRef(false)

  useEffect(() => {
    if (autoAccept && state === 'pending' && onApply && !autoApplied.current) {
      autoApplied.current = true
      onApply(content)
      setState('auto-applied')
    }
  }, [autoAccept, state, onApply, content])

  if (state === 'auto-applied') {
    return (
      <div className={`${classes.root} ${classes.applied} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center gap-2 p-1'>
          <Icon name='check' customSize='16px' />
          <span className={`${classes.appliedLabel} font-size-xs`}>Source auto-applied</span>
        </div>
      </div>
    )
  }

  if (state === 'dismissed') {
    return (
      <div className={`${classes.root} ${classes.dismissed} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className={`${classes.dismissedLabel} font-size-xs p-1`}>Proposed smart endpoint dismissed</div>
      </div>
    )
  }

  if (state === 'applied') {
    return (
      <div className={`${classes.root} ${classes.applied} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center gap-2 p-1'>
          <Icon name='check' customSize='16px' />
          <span className={`${classes.appliedLabel} font-size-xs`}>Source applied</span>
        </div>
      </div>
    )
  }

  const handleApply = (src) => {
    if (onApply) onApply(src)
    setState('applied')
    setShowDiff(false)
  }

  return (
    <>
      <div className={`${classes.root} flex flex-col gap-2 radius-lg shadow-sm my-3 p-3`}>
        <div className='flex items-center justify-between gap-2'>
          <div className={`${classes.titleRow} flex items-center gap-2`}>
            <Icon name='document' customSize='20px' />
            <span className='font-size-sm font-weight-semibold'>Proposed Smart Endpoint</span>
          </div>
          <span className={`${classes.charCount} font-size-xs`}>{content.length.toLocaleString()} chars</span>
        </div>
        {description && (
          <div className={`${classes.description} font-size-xs`}>{description}</div>
        )}
        <div className={classes.divider} />
        <div className='flex items-center justify-between gap-2'>
          <SydButton size='xs' variant='outline' onClick={() => setState('dismissed')}>
            Dismiss
          </SydButton>
          <div className='flex gap-2'>
            <SydButton
              size='xs'
              variant='outline'
              onClick={() => {
                navigator.clipboard.writeText(content).then(() => {
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                }).catch(() => { /* clipboard unavailable */ })
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </SydButton>
            {onApply && (
              <SydButton size='xs' variant='ai' onClick={() => setShowDiff(true)}>
                Review Changes
              </SydButton>
            )}
          </div>
        </div>
      </div>
      {showDiff && (
        <SourceDiffModal
          currentSource={currentSource || ''}
          proposedSource={content}
          description={description}
          onApply={handleApply}
          onDismiss={() => setShowDiff(false)}
        />
      )}
    </>
  )
}

ProposedSmartEndpointCard.propTypes = {
  content: PropTypes.string.isRequired,
  description: PropTypes.string,
  currentSource: PropTypes.string,
  onApply: PropTypes.func,
  autoAccept: PropTypes.bool
}

export default ProposedSmartEndpointCard
